import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import { Pagination } from 'semantic-ui-react';
import {
	getJourneysAdmin,
	deleteJourney,
	duplicateJourney,
	bulkUpdateJourneyAvailability,
} from '../../../api/contentAdminApi';
import { isAllYearAvailability, seasonsLabel, storedAvailableMonths } from '../../../utils/bookingAvailability';
import JourneyAvailabilityModal from './JourneyAvailabilityModal';
import '../../../styles/admin-cms.scss';

function statusBadge(status, published) {
	const value = status || (published ? 'published' : 'draft');
	const labels = {
		draft: 'Draft',
		review: 'In review',
		published: 'Published',
		archived: 'Archived',
	};
	const cls = `admin-cms-status admin-cms-status--${value.replace('_', '-')}`;
	return <span className={cls}>{labels[value] || value}</span>;
}

function journeyDetails(row) {
	const parts = [row.region];
	if (row.priority != null) parts.push(`Priority ${row.priority}`);
	if (row.featured) parts.push('Featured');
	return parts.filter(Boolean).join(' · ');
}

export default function JourneysAdminList() {
	const history = useHistory();
	const location = useLocation();
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState({});
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [availabilityFilter, setAvailabilityFilter] = useState('all');
	const [selected, setSelected] = useState({});
	const [savingMonths, setSavingMonths] = useState(false);
	const [modal, setModal] = useState(null);

	const fetchRows = useCallback((page = 1) => {
		setLoading(true);
		getJourneysAdmin({ page, admin_list: 'true', ...(search ? { search } : {}) })
			.then((res) => {
				const data = res.data || {};
				setRows(Array.isArray(data.journeys) ? data.journeys : []);
				setMeta(data.meta || {});
			})
			.catch(() => setRows([]))
			.finally(() => setLoading(false));
	}, [search]);

	useEffect(() => {
		const page = new URLSearchParams(location.search).get('page') || 1;
		fetchRows(Number(page));
	}, [fetchRows, location.search]);

	const filteredRows = useMemo(() => {
		if (availabilityFilter === 'seasonal') {
			return rows.filter((row) => !isAllYearAvailability(row));
		}
		if (availabilityFilter === 'all_year') {
			return rows.filter((row) => isAllYearAvailability(row));
		}
		return rows;
	}, [rows, availabilityFilter]);

	const selectedSlugs = useMemo(
		() => Object.keys(selected).filter((slug) => selected[slug]),
		[selected]
	);

	const toggleAll = () => {
		if (selectedSlugs.length === filteredRows.length) {
			setSelected({});
		} else {
			const next = {};
			filteredRows.forEach((row) => { next[row.slug] = true; });
			setSelected(next);
		}
	};

	const openModalForRows = (targetRows, title, subtitle) => {
		const slugs = targetRows.map((r) => r.slug);
		const sharedMonths = slugs.length === 1
			? storedAvailableMonths(targetRows[0])
			: [];
		setModal({ slugs, title, subtitle, initialMonths: sharedMonths });
	};

	const saveAvailability = (months) => {
		if (!modal?.slugs?.length) return;
		setSavingMonths(true);
		bulkUpdateJourneyAvailability(modal.slugs, months)
			.then(() => {
				swal('Saved', `Availability updated for ${modal.slugs.length} journey${modal.slugs.length !== 1 ? 's' : ''}.`, 'success');
				setModal(null);
				setSelected({});
				fetchRows(Number(new URLSearchParams(location.search).get('page') || 1));
			})
			.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not save availability', 'error'))
			.finally(() => setSavingMonths(false));
	};

	const handleDelete = (row) => {
		swal({
			title: 'Delete this journey?',
			text: 'This cannot be undone.',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then((ok) => {
			if (!ok) return;
			deleteJourney(row.slug)
				.then(() => {
					swal('Deleted', '', 'success');
					fetchRows();
				})
				.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not delete', 'error'));
		});
	};

	const pag = meta.pagination || meta || {};
	const totalPages = pag.total_pages || 1;
	const currentPage = pag.current_page || 1;

	return (
		<div className="admin-cms-list">
			<Link to="/admin/cms" className="admin-cms-back">← Content</Link>
			<header className="admin-cms-list__head">
				<div>
					<h1>Journeys</h1>
					<p>
						Manage booking availability months here — no need to open each journey.
						Changes sync with the edit form and customer booking calendar.
					</p>
				</div>
				<Link to="/admin/cms/journey_form" className="btn btn-primary">+ Add new</Link>
			</header>

			<div className="admin-cms-list__filters admin-cms-list__filters--wrap">
				<input
					type="search"
					placeholder="Search by title…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
				/>
				<select
					className="form-control form-control-sm admin-cms-list__select"
					value={availabilityFilter}
					onChange={(e) => setAvailabilityFilter(e.target.value)}
					aria-label="Filter by availability"
				>
					<option value="all">All availability</option>
					<option value="seasonal">Seasonal only</option>
					<option value="all_year">All year only</option>
				</select>
				<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fetchRows(1)}>Search</button>
			</div>

			{selectedSlugs.length > 0 && (
				<div className="admin-cms-bulk-bar">
					<span>{selectedSlugs.length} selected</span>
					<button
						type="button"
						className="btn btn-sm btn-primary"
						onClick={() => {
							const targetRows = filteredRows.filter((r) => selected[r.slug]);
							openModalForRows(
								targetRows,
								`Set availability (${targetRows.length} journeys)`,
								'Same months will apply to all selected journeys.'
							);
						}}
					>
						Set months for selected
					</button>
					<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelected({})}>
						Clear selection
					</button>
				</div>
			)}

			{loading ? (
				<p className="admin-cms-muted">Loading…</p>
			) : (
				<div className="table-responsive">
					<table className="table admin-cms-table">
						<thead>
							<tr>
								<th className="admin-cms-table__check">
									<input
										type="checkbox"
										className="admin-cms-table__checkbox"
										checked={filteredRows.length > 0 && selectedSlugs.length === filteredRows.length}
										onChange={toggleAll}
										aria-label="Select all on this page"
									/>
								</th>
								<th>Title</th>
								<th>Details</th>
								<th>Availability</th>
								<th>Status</th>
								<th>Updated</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{filteredRows.length === 0 && (
								<tr><td colSpan={7} className="admin-cms-muted">No journeys match this filter.</td></tr>
							)}
							{filteredRows.map((row) => {
								const label = seasonsLabel(row);
								const seasonal = !isAllYearAvailability(row);
								return (
									<tr key={row.slug} className={selected[row.slug] ? 'admin-cms-table__row--selected' : ''}>
										<td className="admin-cms-table__check">
											<input
												type="checkbox"
												className="admin-cms-table__checkbox"
												checked={!!selected[row.slug]}
												onChange={(e) => setSelected((s) => ({ ...s, [row.slug]: e.target.checked }))}
												aria-label={`Select ${row.title}`}
											/>
										</td>
										<td>
											<strong>{row.title}</strong>
											<br />
											<small className="text-muted">{row.slug}</small>
										</td>
										<td>{journeyDetails(row)}</td>
										<td>
											<button
												type="button"
												className={`admin-cms-availability-badge${seasonal ? ' admin-cms-availability-badge--seasonal' : ''}`}
												onClick={() => openModalForRows([row], `Availability: ${row.title}`, row.slug)}
												title="Click to change months"
											>
												{label}
											</button>
										</td>
										<td>{statusBadge(row.editorial_status, row.published)}</td>
										<td>{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</td>
										<td className="admin-cms-table__actions">
											<Link to={`/admin/cms/journey_form/${row.slug}`} className="btn btn-sm btn-outline-primary">Edit</Link>
											<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => duplicateJourney(row.slug).then(() => fetchRows()).catch(() => swal('Error', 'Could not duplicate', 'error'))}>Duplicate</button>
											<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>Delete</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{totalPages > 1 && (
				<Pagination
					boundaryRange={1}
					siblingRange={1}
					activePage={currentPage}
					totalPages={totalPages}
					onPageChange={(e, { activePage }) => {
						history.push(`${location.pathname}?page=${activePage}`);
					}}
				/>
			)}

			<JourneyAvailabilityModal
				key={modal?.slugs?.join(',') || 'closed'}
				open={!!modal}
				title={modal?.title || 'Availability'}
				subtitle={modal?.subtitle}
				initialMonths={modal?.initialMonths || []}
				legacyMode
				saving={savingMonths}
				onCancel={() => setModal(null)}
				onSave={saveAvailability}
			/>
		</div>
	);
}
